import type { MessageDescriptor } from "react-intl";
import { defineMessage, defineMessages } from "react-intl";

import { Dimensions, MetricTypes } from "../autogen/metrics";

export enum MetaColumn {
  EXPAND_CONTEXT = "EXPAND_CONTEXT",
}

export const getMetricTypeString = (
  metricType: MetricTypes | Dimensions | MetaColumn,
  type: "name" | "tooltip" = "name"
): MessageDescriptor => {
  if (
    (
      [
        MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_BY_REFUNDED_DATE_PLUS_SHIPPING_PLUS_TAX,
        MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_FROM_ORDERS_PLUS_SHIPPING_PLUS_TAX,
      ] as (MetricTypes | Dimensions | MetaColumn)[]
    ).indexOf(metricType) > -1
  ) {
    return type === "name"
      ? defineMessage({
          id: "Metric Name: Gross revenue less discounts and refunds, plus shipping and tax",
          defaultMessage: "Total Sales",
        })
      : defineMessage({
          id: "Metric Tooltip: Gross revenue less discounts and refunds, plus shipping and tax",
          defaultMessage: "Gross sales minus discounts and returns plus taxes and shipping",
        });
  }

  if (type === "name") {
    return defineMessages({
      [Dimensions.CHANNEL]: {
        id: "Metric Name: Channel",
        defaultMessage: "Channel",
      },
      [MetricTypes.NEW_CUSTOMERS]: {
        id: "Metric Name: New customers",
        defaultMessage: "New Customers",
      },
      [MetricTypes.TOTAL_ORDER_QUANTITY]: {
        id: "Metric Name: Total Order Quantity",
        defaultMessage: "Total Order Quantity",
      },
      [MetricTypes.REFUNDS_PERCENT]: {
        id: "Metric Name: Refunds %",
        defaultMessage: "Refunds %",
      },
      [MetricTypes.COGS]: {
        id: "Metric Name: Cost of Goods Sold",
        defaultMessage: "Cost of Goods Sold",
      },
      [MetricTypes.GROSS_PROFIT]: {
        id: "Metric Name: Gross Profit",
        defaultMessage: "Gross Profit",
      },
      [MetricTypes.GROSS_MARGIN_PERCENTAGE]: {
        id: "Metric Name: Gross Margin Percentage",
        defaultMessage: "Gross Margin %",
      },
      [Dimensions.ADSET_ID]: {
        id: "Metric Name: ADSET_ID",
        defaultMessage: "Adset ID",
      },
      [MetricTypes.ADSET_NAME]: {
        id: "Metric Name: ADSET_NAME",
        defaultMessage: "Adset Name",
      },
      [MetricTypes.META_AD_SPEND]: {
        id: "Metric Name: META_AD_SPEND",
        defaultMessage: "Spend",
      },
      [MetricTypes.META_AD_CTR]: {
        id: "Metric Name: META_AD_CTR",
        defaultMessage: "Click-Through-Rate",
      },
      [MetricTypes.META_AD_COST_PER_MILLE]: {
        id: "Metric Name: META_AD_COST_PER_MILLE",
        defaultMessage: "Cost Per Mille",
      },
      [MetricTypes.META_AD_COST_PER_CLICK]: {
        id: "Metric Name: META_AD_COST_PER_CLICK",
        defaultMessage: "Cost Per Click",
      },
      [MetricTypes.META_AD_COST_PER_CONVERSION]: {
        id: "Metric Name: META_AD_COST_PER_CONVERSION",
        defaultMessage: "Cost Per Conversion",
      },
      [MetricTypes.META_AD_ATTRIBUTED_CONVERSION_ROAS]: {
        id: "Metric Name: META_AD_ATTRIBUTED_CONVERSION_ROAS",
        defaultMessage: "Attributed ROAS",
      },
      [MetricTypes.META_AD_ATTRIBUTED_CONVERSION_COUNT]: {
        id: "Metric Name: META_AD_ATTRIBUTED_CONVERSION_COUNT",
        defaultMessage: "Conversion Count",
      },
      [MetaColumn.EXPAND_CONTEXT]: {
        id: "Table Column: Expand Context",
        defaultMessage: "See more...",
      },
      [MetricTypes.CAMPAIGN_OBJECTIVE]: {
        id: "Metric Name: CAMPAIGN_OBJECTIVE",
        defaultMessage: "Campaign Objective",
      },
      [MetricTypes.ADSET_AUDIENCE]: {
        id: "Metric Name: ADSET_AUDIENCE",
        defaultMessage: "Audience Type",
      },
      [MetricTypes.ADSET_REGION]: {
        id: "Metric Name: ADSET_REGION",
        defaultMessage: "Region",
      },
      [MetricTypes.ADSET_FIRST_USED]: {
        id: "Metric Name: ADSET_FIRST_USED",
        defaultMessage: "First used",
      },
      [MetricTypes.ADSET_LAST_USED]: {
        id: "Metric Name: ADSET_LAST_USED",
        defaultMessage: "Last used",
      },
    })[metricType];
  } else {
    return defineMessages({
      [Dimensions.CHANNEL]: {
        id: "Metric Tooltip: Channel",
        defaultMessage: "The source of revenue",
      },
      [MetricTypes.NEW_CUSTOMERS]: {
        id: "Metric Tooltip: New customers",
        defaultMessage: "The percentage of orders made by new customers",
      },
      [MetricTypes.TOTAL_ORDER_QUANTITY]: {
        id: "Metric Tooltip: Total Order Quantity",
        defaultMessage: "The total number of orders",
      },
      [MetricTypes.REFUNDS_PERCENT]: {
        id: "Metric Tooltip: Refunds %",
        defaultMessage: "The percentage of the total sold items that have subsequently been returned or refunded",
      },
      [MetricTypes.COGS]: {
        id: "Metric Tooltip: Cost of Goods Sold",
        defaultMessage: "The total cost of all sold products",
      },
      [MetricTypes.GROSS_PROFIT]: {
        id: "Metric Tooltip: Gross Profit",
        defaultMessage: "Total sales minus the cost of goods sold and shipping costs",
      },
      [MetricTypes.GROSS_MARGIN_PERCENTAGE]: {
        id: "Metric Tooltip: Gross margin percentage",
        defaultMessage: "Total sales minus the gross profit, divided by the total sales",
      },
      [Dimensions.ADSET_ID]: {
        id: "Metric Tooltip: ADSET_ID",
        defaultMessage: "The id of the adset",
      },
      [MetricTypes.ADSET_NAME]: {
        id: "Metric Tooltip: ADSET_NAME",
        defaultMessage: "The name of the adset",
      },
      [MetricTypes.META_AD_SPEND]: {
        id: "Metric Tooltip: META_AD_SPEND",
        defaultMessage: "The amount spent on the adset in this window of time",
      },
      [MetricTypes.META_AD_CTR]: {
        id: "Metric Tooltip: META_AD_CTR",
        defaultMessage: "The proportion of impressions that resulted in a click",
      },
      [MetricTypes.META_AD_COST_PER_MILLE]: {
        id: "Metric Tooltip: META_AD_COST_PER_MILLE",
        defaultMessage: "The average cost of a thousand impresssions",
      },
      [MetricTypes.META_AD_COST_PER_CLICK]: {
        id: "Metric Tooltip: META_AD_COST_PER_CLICK",
        defaultMessage: "The average cost of a single click",
      },
      [MetricTypes.META_AD_COST_PER_CONVERSION]: {
        id: "Metric Tooltip: META_AD_COST_PER_CONVERSION",
        defaultMessage: "The average cost of a conversion",
      },
      [MetricTypes.META_AD_ATTRIBUTED_CONVERSION_COUNT]: {
        id: "Metric Tooltip: META_AD_ATTRIBUTED_CONVERSION_COUNT",
        defaultMessage: "The count of all reported conversions",
      },
      [MetricTypes.META_AD_ATTRIBUTED_CONVERSION_ROAS]: {
        id: "Metric Tooltip: META_AD_ATTRIBUTED_CONVERSION_ROAS",
        defaultMessage: "The ratio of amount spent on this adset to the reported conversion value",
      },
      [MetricTypes.CAMPAIGN_OBJECTIVE]: {
        id: "Metric Tooltip: CAMPAIGN_OBJECTIVE",
        defaultMessage: "The objective of the parent campaign",
      },
      [MetricTypes.ADSET_AUDIENCE]: {
        id: "Metric Tooltip: ADSET_AUDIENCE",
        defaultMessage: "The classification of the adset's audience targeting",
      },
      [MetricTypes.ADSET_REGION]: {
        id: "Metric Tooltip: ADSET_REGION",
        defaultMessage: "The region targeted by the adset",
      },
      [MetricTypes.ADSET_FIRST_USED]: {
        id: "Metric Tooltip: ADSET_FIRST_USED",
        defaultMessage: "The date the adset was first used",
      },
      [MetricTypes.ADSET_LAST_USED]: {
        id: "Metric Tooltip: ADSET_LAST_USED",
        defaultMessage: "The date the adset was most recently used",
      },
      [MetaColumn.EXPAND_CONTEXT]: {
        id: "Table Column Tooltip: Expand Context",
        defaultMessage: "Would you like to know more? Click to expand",
      },
    })[metricType];
  }
};
